//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type Ref } from 'react';

import { Box, Button, Paper, Typography } from '@mui/material';

import { type ApprovalDecision } from '@/agent/contract';
import { type ChatApproval } from './ChatTypes';

interface ApprovalCardProps {
  approval: ChatApproval;
  // A decision has been sent and its turn is running.
  isBusy: boolean;
  onDecide: (decision: ApprovalDecision) => void;
  // The transcript's handle on the card, for the one caller that moves
  // focus here.  Whether this question is one to interrupt the user for is
  // not a thing the card can know: a reopened conversation renders the same
  // undecided card as a turn that has just paused.
  ref?: Ref<HTMLDivElement>;
}

const DECIDED_TEXT: Record<Exclude<ChatApproval['decision'], null>, string> = {
  approved: 'Approved',
  denied: 'Denied',
  expired: 'Expired before a decision was made'
};

// The agent asked before doing something irreversible.  The card stays
// after the decision so the transcript reads truthfully.
const ApprovalCard = ({
  approval, isBusy, onDecide, ref
}: ApprovalCardProps) => {
  return (
    <Paper
      ref={ref}
      role="group"
      tabIndex={-1}
      aria-label={`Approval requested: ${approval.summary}`}
      variant="outlined"
      sx={{ mx: '17px', my: 1, p: 1.5, borderColor: 'warning.main' }}
    >
      <Typography variant="subtitle2">Approval requested</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>{approval.summary}</Typography>
      {approval.decision === null ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            disabled={isBusy}
            aria-label={`Approve: ${approval.summary}`}
            onClick={() => onDecide('approved')}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={isBusy}
            aria-label={`Deny: ${approval.summary}`}
            onClick={() => onDecide('denied')}
          >
            Deny
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {DECIDED_TEXT[approval.decision]}
        </Typography>
      )}
    </Paper>
  );
};

export default ApprovalCard;
